import type { ApiErrorBody, ErrorCode } from '@lp/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
  upload<T>(path: string, file: File, extra?: Record<string, string>): Promise<T>;
}

/**
 * Same-origin JSON client. Every call carries `x-lp-csrf: 1`; the backend refuses state-changing
 * requests without it, which a cross-site form or image tag cannot add.
 */
export function createApi(base: string, onUnauthenticated?: () => void): ApiClient {
  async function call<T>(method: string, path: string, body?: BodyInit, json = true): Promise<T> {
    const headers: Record<string, string> = { 'x-lp-csrf': '1', accept: 'application/json' };
    if (json && body !== undefined) headers['content-type'] = 'application/json';
    const res = await fetch(base + path, { method, headers, body, credentials: 'same-origin' });
    if (res.status === 204) return undefined as T;
    const data = (await res.json().catch(() => null)) as unknown;
    if (!res.ok) {
      const err = (data as ApiErrorBody | null)?.error;
      if (res.status === 401 && onUnauthenticated) onUnauthenticated();
      throw new ApiError(res.status, err?.code ?? 'INTERNAL', err?.message ?? res.statusText, err?.fields);
    }
    return data as T;
  }
  return {
    get: (p) => call('GET', p),
    post: (p, b) => call('POST', p, b === undefined ? undefined : JSON.stringify(b)),
    put: (p, b) => call('PUT', p, b === undefined ? undefined : JSON.stringify(b)),
    del: (p) => call('DELETE', p),
    upload: (p, file, extra = {}) => {
      const form = new FormData();
      form.set('file', file);
      for (const [k, v] of Object.entries(extra)) form.set(k, v);
      return call('POST', p, form, false);
    },
  };
}
